import { darken, desaturate, getContrast, mix, parseToRgba, toHex } from 'color2k';
import kelvinToRgb from 'kelvin-to-rgb';
import { LightState } from 'types/aspen/state';
import { AppDeviceLeaf } from 'types/common/leaf';
import { yiq } from 'yiq';

export interface RGBColor {
  r: number;
  g: number;
  b: number;
}
interface RGBAColor extends RGBColor {
  a: number;
}

export const DEFAULT_COLOR = {
  rgb: [102, 47, 142],
  temperature: 2700,
};

const BRIGHTNESS_THRESHOLDS = {
  LOW_BRIGHTNESS: 0.25,
  MAX_DIMMING: 0.65,
  LOW_DIMMING: 0.4,
  HIGH_SHADOW_INTENSITY: 0.5,
  LOW_SHADOW_INTENSITY: 0.3,
} as const;

const TEMPERATURE_INFLUENCE = {
  MIN: 0.15,
  MAX: 0.5,
} as const;

const DARK_VARIANT_INFLUENCE = {
  MAX_DARKNESS: 0.6,
  MIN_DARKNESS: 0.45,
  LOW_SATURATION: 0.15,
  HIGH_SATURATION: 0.25,
} as const;

export interface ContrastAnalysis {
  lightColor: string;
  darkColor: string;
  whiteWorstCase: number;
  darkWorstCase: number;
  preferLight: boolean;
}

export const normalizeRGBAFormat = (rgbValue: number[] | RGBColor | RGBAColor): RGBAColor => {
  if (Array.isArray(rgbValue)) {
    const [r = 0, g = 0, b = 0, a = 1] = rgbValue;
    return { r, g, b, a };
  }
  if ('a' in rgbValue) {
    return rgbValue;
  }
  const { r = 0, g = 0, b = 0 } = rgbValue;
  return { r, g, b, a: 1 };
};

export const kelvinToRGB = (kelvin: number): RGBColor => normalizeRGBAFormat(kelvinToRgb(kelvin)) as RGBColor;

export const rgbToHex = ({ r, g, b }: RGBColor): string => toHex(rgbToString({ r, g, b })).toLowerCase();

export const rgbToString = ({ r, g, b }: RGBColor): string =>
  `rgb(${Math.round(r)}, ${Math.round(g)}, ${Math.round(b)})`;

export const rgbToHsl = (rgbColor: string): { h: number; s: number; l: number } => {
  try {
    const [r, g, b] = parseToRgba(rgbColor);
    const rNorm = r / 255;
    const gNorm = g / 255;
    const bNorm = b / 255;

    const max = Math.max(rNorm, gNorm, bNorm);
    const min = Math.min(rNorm, gNorm, bNorm);
    const diff = max - min;

    let h = 0;
    let s = 0;
    const l = (max + min) / 2;

    if (diff !== 0) {
      s = l > 0.5 ? diff / (2 - max - min) : diff / (max + min);

      switch (max) {
        case rNorm:
          h = ((gNorm - bNorm) / diff + (gNorm < bNorm ? 6 : 0)) / 6;
          break;
        case gNorm:
          h = ((bNorm - rNorm) / diff + 2) / 6;
          break;
        case bNorm:
          h = ((rNorm - gNorm) / diff + 4) / 6;
          break;
        default:
          h = 0;
          break;
      }
    }

    return { h: h * 360, s: s * 100, l: l * 100 };
  } catch {
    return { h: 0, s: 0, l: 50 };
  }
};

export const getColorProperties = (leafState: LightState, brightnessLevel: number) => {
  const {
    colorType,
    rgb: rgbState = DEFAULT_COLOR.rgb,
    temperature: temperatureState = DEFAULT_COLOR.temperature,
  } = leafState.color;

  const kelvinRGB = normalizeRGBAFormat(kelvinToRGB(temperatureState) as RGBColor) as RGBColor;
  const rgbColor = normalizeRGBAFormat(rgbState) as RGBColor;
  const powerState = leafState?.power?.power;
  const brightnessFactor = brightnessLevel / 100;
  const colorRgb = colorType === 'rgb' ? rgbColor : kelvinRGB;

  return { colorRgb, brightnessFactor, powerState, kelvinRGB, rgbColor, colorType, temperatureState };
};

export const createRgbGradient = (baseColor: string, brightnessFactor: number) => {
  const darkVariant = generateDarkVariant(baseColor);
  // At 0% brightness: mix 70% dark, 30% base to show original color
  // At 100% brightness: mix 0% dark, 100% base
  const isDark = isDarkColor(baseColor);
  const maxDimming = isDark ? BRIGHTNESS_THRESHOLDS.MAX_DIMMING : BRIGHTNESS_THRESHOLDS.LOW_DIMMING;
  const shadowIntensity = isDark
    ? BRIGHTNESS_THRESHOLDS.HIGH_SHADOW_INTENSITY
    : BRIGHTNESS_THRESHOLDS.LOW_SHADOW_INTENSITY;
  const mixAmount = Math.max(0, maxDimming - brightnessFactor * maxDimming);
  const dimmedColor = mix(baseColor, darkVariant, mixAmount);
  const shadowColor = mix(dimmedColor, darkVariant, shadowIntensity);
  return { shadowColor, dimmedColor };
};

export const createTempGradient = (
  baseColor: string,
  temperatureState: number,
  brightnessFactor: number,
  brightnessLevel: number,
) => {
  const tempColor = getTempColor(temperatureState || DEFAULT_COLOR.temperature, brightnessLevel);
  // Temperature influence: 15% at 0% brightness, 80% at 100% brightness
  const minInfluence = TEMPERATURE_INFLUENCE.MIN;
  const maxInfluence = TEMPERATURE_INFLUENCE.MAX;
  const tempInfluenceRange = minInfluence + brightnessFactor * (maxInfluence - minInfluence);

  let blendedColor = mix(baseColor, tempColor, tempInfluenceRange);

  // Dimming for very low brightness (below 25%)
  if (brightnessFactor < BRIGHTNESS_THRESHOLDS.LOW_BRIGHTNESS) {
    const extraDimming = (BRIGHTNESS_THRESHOLDS.LOW_BRIGHTNESS - brightnessFactor) * 1.25;
    blendedColor = darken(blendedColor, extraDimming);
  }
  return { blendedColor, baseColor };
};

export const createGroupGradient = (memberLeaves: AppDeviceLeaf[]): string => {
  if (!memberLeaves.length) {
    return 'linear-gradient(to right, #f0f0f0, #e0e0e0)';
  }

  // Extract colors and sort by hue for natural spectrum flow
  const colors = memberLeaves
    .map((leaf) => {
      const leafState = leaf.state as LightState;
      const powerState = leafState?.power?.power || 'off';
      const brightnessLevel = leafState.brightness?.level || 0;

      if (powerState === 'off') {
        return { color: '#d0d0d0', hue: 0, isOff: true };
      }

      const { kelvinRGB, rgbColor, colorType, temperatureState } = getColorProperties(leafState, brightnessLevel);
      const baseColor = colorType === 'rgb' ? rgbToString(rgbColor) : rgbToString(kelvinRGB as RGBColor);

      // Get the light color (dimmed/base) for soft blending
      const lightColor =
        colorType === 'rgb'
          ? createRgbGradient(baseColor, brightnessLevel / 100).dimmedColor
          : createTempGradient(
              baseColor,
              temperatureState || DEFAULT_COLOR.temperature,
              brightnessLevel / 100,
              brightnessLevel,
            ).baseColor;

      const hue = rgbToHsl(baseColor).h;
      return { color: lightColor, hue, isOff: false };
    })
    .sort((a, b) => a.hue - b.hue); // hue-based sorting for spectrum harmony

  // Create gradient
  const colorStops = colors.map((item, index) => {
    const position = colors.length === 1 ? 50 : (index * 100) / (colors.length - 1);
    return `${item.color} ${position}%`;
  });

  return `linear-gradient(to right, ${colorStops.join(', ')})`;
};

export const getContrastingText = (
  backgroundColor: RGBColor,
  brightnessFactor: number,
  brightnessLevel: number,
  colorType: string,
  temperatureState?: number,
): string => {
  const { preferLight } = getContrastAnalysis(
    backgroundColor,
    brightnessFactor,
    brightnessLevel,
    colorType,
    temperatureState,
  );

  return preferLight ? '#f5f5f5' : '#0b001b';
  // Test if each text color works across the entire gradient
};

export const adjustTextColor = (
  colorRgb: RGBColor,
  brightnessFactor: number,
  brightnessLevel: number,
  colorType: string,
  temperatureState: number,
  powerState: 'on' | 'off' | undefined,
) => {
  if (powerState === 'on') {
    return getContrastingText(colorRgb, brightnessFactor, brightnessLevel, colorType, temperatureState);
  }
  return '#f5f5f5';
};

export const getContrastAnalysis = (
  backgroundColor: RGBColor,
  brightnessFactor: number,
  brightnessLevel: number,
  colorType: string,
  temperatureState?: number,
): ContrastAnalysis => {
  const baseColor = `rgba(${backgroundColor.r}, ${backgroundColor.g}, ${backgroundColor.b}, ${brightnessFactor})`;

  let lightColor: string = baseColor; // top of gradient
  let darkColor: string = baseColor; // bottom of gradient

  if (colorType === 'rgb') {
    const { shadowColor, dimmedColor } = createRgbGradient(baseColor, brightnessFactor);
    lightColor = dimmedColor;
    darkColor = shadowColor;
  } else if (colorType === 'temperature') {
    const { blendedColor, baseColor: tempBaseColor } = createTempGradient(
      baseColor,
      temperatureState || DEFAULT_COLOR.temperature,
      brightnessFactor,
      brightnessLevel,
    );

    lightColor = tempBaseColor;
    darkColor = blendedColor;
  }

  // Get min contrast ratio for both text colors against both gradient colors
  const whiteWorstCase = Math.min(getContrast('#FFFFFF', lightColor), getContrast('#FFFFFF', darkColor));
  const darkWorstCase = Math.min(getContrast('#0b001b', lightColor), getContrast('#0b001b', darkColor));

  return {
    lightColor,
    darkColor,
    whiteWorstCase,
    darkWorstCase,
    preferLight: whiteWorstCase >= darkWorstCase,
  };
};

export const getTempColor = (temp: number, brightness: number) => {
  const lightnessFactor = 0.6 + (brightness / 100) * 0.3; // 60% to 90% lightness

  if (temp < 2700) return `hsl(30, 70%, ${lightnessFactor * 70}%)`;
  if (temp < 3000) return `hsl(45, 50%, ${lightnessFactor * 80}%)`;
  if (temp < 4000) return `hsl(50, 30%, ${lightnessFactor * 85}%)`;
  if (temp < 5000) return `hsl(200, 40%, ${lightnessFactor * 85}%)`;
  return `hsl(210, 50%, ${lightnessFactor * 85}%)`;
};

export const generateDarkVariant = (baseColor: string) => {
  const isDark = isDarkColor(baseColor);
  let darkColor = darken(baseColor, isDark ? DARK_VARIANT_INFLUENCE.MAX_DARKNESS : DARK_VARIANT_INFLUENCE.MIN_DARKNESS);
  darkColor = desaturate(
    darkColor,
    isDark ? DARK_VARIANT_INFLUENCE.LOW_SATURATION : DARK_VARIANT_INFLUENCE.HIGH_SATURATION,
  );
  return darkColor;
};

export const isDarkColor = (baseColor: string) => {
  const hexColor = toHex(baseColor);
  // Strip alpha channel if present (8 chars -> 6 chars)
  const rgbHexColor = hexColor.length === 9 ? hexColor.substring(0, 7) : hexColor;
  return yiq(rgbHexColor) === '#fff';
};
